/**
 * Beheert alle directe interacties met de Firestore 'tasks' collectie, inclusief queries, batches en real-time abonnementen.
 */
// @ts-check
import {
  collection,
  query,
  where,
  onSnapshot,
  doc,
  getDoc,
  getDocs,
  orderBy,
  limit,
  serverTimestamp,
} from 'firebase/firestore';
import { generateKeyBetween } from 'fractional-indexing';
import { db } from '@/lib/firebase';
import { maxOrderKey } from '@/features/tasks/utils/interactions/listKeyHelpers';
import { processDoc, handleTaskRepairs } from './taskMutations';

/**
 * Fetches a single task document.
 * @param {string} taskId
 * @returns {Promise<Task|null>}
 */
export const getTask = async (taskId) => {
  try {
    const taskRef = doc(db, 'tasks', taskId);
    const snap = await getDoc(taskRef);
    if (snap.exists()) {
      return /** @type {Task} */ ({ id: snap.id, ...snap.data() });
    }
    return null;
  } catch (error) {
    console.error(`TaskService: Error fetching task ${taskId}:`, error);
    throw error;
  }
};

/**
 * Gets a task document reference.
 * @param {string} taskId
 * @returns {import('firebase/firestore').DocumentReference}
 */
export const getTaskRef = (taskId) => doc(db, 'tasks', taskId);

/**
 * Gets a new task document reference with an auto-generated ID.
 * @returns {import('firebase/firestore').DocumentReference}
 */
export const getNewTaskRef = () => doc(collection(db, 'tasks'));

/**
 * Fetches all tasks for a given parent and user.
 * @param {string|null} parentId
 * @param {string} userId
 * @returns {Promise<import('firebase/firestore').QueryDocumentSnapshot[]>}
 */
export const getTasksByParent = async (parentId, userId) => {
  try {
    const q = query(
      collection(db, 'tasks'),
      where('userId', '==', userId),
      where('parentId', '==', parentId),
    );
    const snap = await getDocs(q);
    return snap.docs;
  } catch (error) {
    console.error('TaskService: Error fetching tasks by parent:', error);
    throw error;
  }
};

/**
 * Fetches the last sibling task for a given parent.
 * @param {string|null} parentId
 * @param {string} userId
 * @returns {Promise<Object|null>}
 */
export const getLastSiblingTask = async (parentId, userId) => {
  try {
    const q = query(
      collection(db, 'tasks'),
      where('userId', '==', userId),
      where('parentId', '==', parentId),
      orderBy('orderKey', 'desc'),
      limit(1),
    );
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs[0]?.data() || null;
  } catch (error) {
    console.error('TaskService: Error fetching last sibling task:', error);
    throw error;
  }
};

/**
 * The Firestore Web SDK only allows `Transaction.get()` on a single
 * `DocumentReference`, not on a query - so a transaction can't read "the
 * last sibling" directly (that requires a query). Instead, each parent group
 * (`userId` + `parentId`) gets a single anchor document that tracks the last
 * issued orderKey. Reading and writing that one document inside a
 * transaction is what makes append operations serialize correctly: Firestore
 * only detects a conflict for documents actually read-and-written by a
 * transaction, so two concurrent appends racing on the same anchor doc force
 * a retry instead of both generating the same key.
 * @param {string} userId
 * @param {string|null} parentId
 */
export const getOrderAnchorRef = (userId, parentId) =>
  doc(db, 'taskOrderAnchors', `${userId}__${parentId || 'root'}`);

/**
 * Reserves the next orderKey for a parent group inside a Firestore
 * transaction.
 *
 * `fallbackLastKey` is a plain, non-transactional query for the current
 * last sibling. It seeds the anchor the first time a parent group is
 * touched (before any anchor doc exists), and is also folded in as a floor
 * on every call after that — non-append reorders (drag-and-drop, manual
 * edits) write sibling orderKeys straight to the task doc without updating
 * the anchor, so the anchor's `lastOrderKey` can go stale (behind the true
 * last sibling). Without this floor a stale anchor can reissue a key that
 * already exists.
 * @param {import('firebase/firestore').Transaction} transaction
 * @param {string|null} parentId
 * @param {string} userId
 * @param {string|null} fallbackLastKey
 * @param {string|null} [floorKey] - minimum key the new key must come after
 *   (e.g. last visible sibling when appending to a filtered list)
 * @returns {Promise<string>}
 */
export const reserveNextOrderKeyInTransaction = async (
  transaction,
  parentId,
  userId,
  fallbackLastKey,
  floorKey = null,
) => {
  const anchorRef = getOrderAnchorRef(userId, parentId);
  const anchorSnap = await transaction.get(anchorRef);
  const anchorBase = anchorSnap.exists()
    ? (anchorSnap.data()?.lastOrderKey ?? null)
    : null;
  // The anchor only advances through this append path — a drag-and-drop
  // reorder or manual edit can write a sibling orderKey straight to its task
  // doc without ever touching the anchor, leaving it stale (behind the true
  // last sibling). Folding in the freshly-queried fallback as a floor, even
  // when the anchor exists, keeps the anchor from reissuing a key that
  // already exists on such a stale read.
  const lastKey = maxOrderKey(maxOrderKey(anchorBase, fallbackLastKey), floorKey);
  const newKey = generateKeyBetween(lastKey, null);
  transaction.set(anchorRef, {
    lastOrderKey: newKey,
    userId,
    parentId: parentId || null,
    updatedAt: serverTimestamp(),
  });
  return newKey;
};

/**
 * Subscribes to the current user's non-archived tasks in Firestore.
 *
 * @param {string} userId - The user ID to query tasks for.
 * @param {function} onNext - Callback receiving processed document changes.
 * @param {function} onError - Callback for errors.
 * @returns {function} Unsubscribe function.
 */
export const subscribeToTasks = (userId, onNext, onError) => {
  const q = query(
    collection(db, 'tasks'),
    where('userId', '==', userId),
    where('archived', '==', false),
  );

  return onSnapshot(
    q,
    (snapshot) => {
      try {
        // Verwerk documenten BÚITEN de React state updater, zodat eventuele
        // data/parse-fouten synchroon worden opgevangen door het try/catch blok.
        const processedChanges = snapshot.docChanges().map((change) => {
          if (change.type === 'removed') {
            return { type: 'removed', id: change.doc.id };
          }
          const { task, needsWrite } = processDoc(change.doc);
          return {
            type: change.type,
            task,
            needsWrite,
            docRef: change.doc.ref,
          };
        });

        // Build a quick lookup of this snapshot's processed tasks to check parent projects.
        const snapTaskMap = new Map(
          processedChanges
            .filter((c) => c.type !== 'removed')
            .map((c) => [c.task.id, c.task]),
        );

        handleTaskRepairs(processedChanges, snapTaskMap);

        onNext(processedChanges);
      } catch (err) {
        console.error('Error processing snapshot changes:', err);
        if (onError) onError(err);
      }
    },
    (err) => {
      console.error('Critical: Error fetching tasks:', err);
      if (onError) onError(err);
    },
  );
};
